import React from 'react'
import PropTypes from 'prop-types'
import { ErrorBoundary } from '@sainsburys-tech/bolt'
import { canUseDom, debounce } from '@sainsburys-tech/boltui-utils'
import Template from './ListerTools.template'
import { CATEGORY_FILTER_ID } from '../../helpers/CategoryHelper'
import * as Styled from './styles'

export const getPageCountString = (currentPage, pageSize, totalPages, totalData) => {
  const firstNumber = currentPage === 1 ? '1' : ((currentPage - 1) * pageSize + 1).toString()

  let lastNumber
  if (currentPage === totalPages) {
    lastNumber = totalData
  } else {
    lastNumber = currentPage * pageSize
  }

  return Template.pageCount(firstNumber, lastNumber, totalData)
}

class ListerTools extends React.Component {
  constructor(props, context) {
    super(props, context)
    this.listerGroupHidden = false

    this.state = {
      scrollPosition: {
        x: 0,
        y: 0
      },
      scrollingUp: false
    }
  }

  componentDidMount() {
    this.handleScroll = debounce(this.handleScroll, 150)
    window.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  }

  setFilterButtonText() {
    const isFilterShowAll = this.props.appliedFilters.some(filter => this.isFilterShowAllRating(filter))
    if (this.props.searchTerm === 'stitch') {
      return 'Stitch'
    }
    if (
      !this.props.appliedFilters.length ||
      this.isCategoryOnlyFilter() ||
      (this.props.appliedFilters.length === 1 && isFilterShowAll)
    ) {
      return Template.filterBtn
    }
    return Template.editBtn
  }

  getAppliedFilterCount() {
    const filterCount = this.props.appliedFilters
      .filter(filter => filter.id !== 'category')
      .reduce((acc, cv) => {
        if (!this.isFilterShowAllRating(cv)) {
          return cv.values ? acc + cv.values.length : acc
        }
        return acc
      }, 0)
    return filterCount > 0 ? filterCount : ''
  }

  /**
   * @param {Object} sortBy i.e {Price: 'asc'}
   * @returns
   */
  getSelectedSortKey(sortBy) {
    if (sortBy) {
      const key = Object.keys(sortBy)[0]
      return this.getSortByObject(`${key}_${sortBy[key]}`).key
    }
    // default to first sort by key when no sort applied
    return this.props.sortByValues[0].key
  }

  getSortByObject(value) {
    const defaultSortByObject = this.props.sortByValues[0]

    if (!value) {
      return defaultSortByObject
    }

    const matchedSortObject = this.props.sortByValues.find(sortBy => {
      return sortBy.key === value
    })

    return matchedSortObject || defaultSortByObject
  }

  manageFilterPanelDisplay = version => {
    const { scrollPosition, scrollingUp } = this.state
    if (scrollPosition.y > 375 && scrollingUp) {
      this.listerGroupHidden = true
    } else {
      this.listerGroupHidden = false
    }
  }

  calculateScrollDirection = () => {
    if (window.scrollY > this.state.scrollPosition.y) {
      this.setState({
        scrollingUp: false
      })
    } else {
      this.setState({
        scrollingUp: true
      })
    }
  }

  handleScroll = () => {
    this.calculateScrollDirection()
    const payload = canUseDom()
      ? {
          x: window.scrollX || window.pageXOffset,
          y: window.scrollY || window.pageYOffset
        }
      : {
          x: 0,
          y: 0
        }
    this.setState({ scrollPosition: payload })
  }

  onClick = () => {
    this.props.handleShowFilterModalClick()
    window.scrollTo(0, 0)
  }

  /**
   * Returns default sort key if the passed in sort key
   * is not valid
   * @param {String} selectedKey i.e Price_desc
   */
  sortBy(selectedKey) {
    const sortBy = {}
    const { type, order } = this.getSortByObject(selectedKey).value

    sortBy[`${type}`] = order

    this.props.handleSortByChange(this.context, { sortBy })
    window.scrollTo(0, 0)
  }

  isCategoryOnlyFilter() {
    return this.props.appliedFilters.length === 1 && this.props.appliedFilters[0].id === CATEGORY_FILTER_ID
  }

  isFilterShowAllRating(filter) {
    return (
      filter.id === 'customer rating' && filter.values && filter.values.length > 0 && filter.values[0] === 'show all'
    )
  }

  render() {
    const { currentPage, pageSize, totalPages, totalData, products } = this.props
    const { sortByValues } = this.props
    const filterButtonText = this.setFilterButtonText()
    const selectedSort = this.getSelectedSortKey(this.props.appliedSortBy)
    const sortByOptions = sortByValues.map(({ key, label }, index) => (
      <option key={index} value={key} label={label}>
        {label}
      </option>
    ))
    const filterCount = this.getAppliedFilterCount()
    if (this.context.reduxStore && this.context.reduxStore.getState().application) {
      this.manageFilterPanelDisplay()
    }

    return (
      <ErrorBoundary>
        <Styled.ListerTools hidden={this.listerGroupHidden} data-el='search-toolbar'>
          <Styled.FilterButton className='xs-6--none md-4 lg-hidden' onClick={this.onClick} data-el='edit-filters'>
            <Styled.FilterButtonLabel data-test='filter-button__label'>{filterButtonText}</Styled.FilterButtonLabel>
            <Styled.FilterButtonCount data-search-results={filterCount}>
              {filterCount > 0 ? `(${filterCount})` : ''}
            </Styled.FilterButtonCount>
            <Styled.FilterButtonIcon width='1.5em' height='1.5em' fill='currentColor' />
          </Styled.FilterButton>
          <Styled.ResultsCount
            className='md-4 lg-6 xs-hidden md-block'
            data-search-results={this.props.totalData}
            tabIndex='0'
            role='textbox'
            aria-readonly='true'>
            {getPageCountString(currentPage, pageSize, totalPages, totalData, products)}
          </Styled.ResultsCount>
          <Styled.SortPanel htmlFor='sort-select' className='xs-6 md-4 lg-6--none'>
            <Styled.SortBy className='lg-2--none'>{Template.sortBy} </Styled.SortBy>
            <Styled.SortPanel margin>
              <Styled.SortIcon width='1.5em' height='1.5em' fill='currentColor' dir='down' />
              <Styled.SortSelect
                id='sort-select'
                value={selectedSort}
                data-el='sort-select'
                onChange={e => this.sortBy(e.target.value)}>
                {sortByOptions}
              </Styled.SortSelect>
            </Styled.SortPanel>
          </Styled.SortPanel>
          <Styled.ResultsCount as='span' sticky data-search-results={this.props.totalData} tabIndex='0'>
            {getPageCountString(currentPage, pageSize, totalPages, totalData, products)}
          </Styled.ResultsCount>
        </Styled.ListerTools>
      </ErrorBoundary>
    )
  }
}

ListerTools.propTypes = {
  appliedSortBy: PropTypes.object,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalData: PropTypes.number.isRequired,
  sortByValues: PropTypes.array.isRequired,
  appliedFilters: PropTypes.array.isRequired,
  searchTerm: PropTypes.string,
  handleShowFilterModalClick: PropTypes.func,
  handleSortByChange: PropTypes.func,
  products: PropTypes.array
}

ListerTools.contextTypes = {
  reduxStore: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func
  })
}

ListerTools.defaultProps = {
  searchTerm: '',
  products: [],
  appliedSortBy: {},
  handleShowFilterModalClick: () => {},
  handleSortByChange: () => {}
}

export default ListerTools
